import { worksheetDocumentSchema } from './document.js';
import { FONT_IDS } from './fonts.js';
export class DocumentValidationError extends Error {
    problems;
    constructor(problems) {
        super(`Dokument ist ungültig:\n${problems.map((p) => `  • ${p.path ? `${p.path}: ` : ''}${p.message}`).join('\n')}`);
        this.problems = problems;
        this.name = 'DocumentValidationError';
    }
}
/** Wandelt einen Zod-Pfad in eine lesbare Punkt-/Klammer-Schreibweise. */
export function formatPath(path) {
    return path
        .map((part) => (typeof part === 'number' ? `[${part}]` : `.${String(part)}`))
        .join('')
        .replace(/^\./, '');
}
export function formatIssues(error) {
    return error.issues.map((issue) => ({
        path: formatPath(issue.path),
        message: issue.message,
    }));
}
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/**
 * Referenzprüfungen, die das Struktur-Schema nicht abdecken: Seitenrahmen,
 * Gruppen, Assets, Verankerungen, Textfluss und doppelte IDs.
 */
export function checkReferences(doc) {
    const problems = [];
    const itemIds = new Set();
    const itemTypes = new Map();
    doc.pages.forEach((page, pageIndex) => {
        const at = (suffix) => `pages[${pageIndex}]${suffix}`;
        if (page.frameId && !doc.frames[page.frameId]) {
            problems.push({
                path: at('.frameId'),
                message: `Seitenrahmen "${page.frameId}" ist im Dokument nicht definiert`,
            });
        }
        const guideIds = new Set(page.guides.map((guide) => guide.id));
        page.items.forEach((item, itemIndex) => {
            const base = at(`.items[${itemIndex}]`);
            if (itemIds.has(item.id)) {
                problems.push({ path: `${base}.id`, message: `Feld-ID "${item.id}" kommt mehrfach vor` });
            }
            itemIds.add(item.id);
            itemTypes.set(item.id, item.type);
            if (item.groupId && !doc.groups[item.groupId]) {
                problems.push({
                    path: `${base}.groupId`,
                    message: `Gruppe "${item.groupId}" ist im Dokument nicht definiert`,
                });
            }
            for (const [anchorIndex, anchor] of (item.anchors ?? []).entries()) {
                if (!guideIds.has(anchor.guideId)) {
                    problems.push({
                        path: `${base}.anchors[${anchorIndex}].guideId`,
                        message: `Layoutlinie "${anchor.guideId}" gibt es auf dieser Seite nicht`,
                    });
                }
            }
            const requireAsset = (assetId, path) => {
                if (!doc.assets[assetId]) {
                    problems.push({ path, message: `Asset "${assetId}" fehlt im Dokument` });
                }
            };
            if (item.type === 'image')
                requireAsset(item.assetId, `${base}.assetId`);
            if (item.type === 'solutionImage') {
                requireAsset(item.correctAssetId, `${base}.correctAssetId`);
                item.distractorAssetIds.forEach((assetId, i) => requireAsset(assetId, `${base}.distractorAssetIds[${i}]`));
            }
            if (item.type === 'audio') {
                if (item.soundAssetId)
                    requireAsset(item.soundAssetId, `${base}.soundAssetId`);
                if (!item.soundAssetId && !item.speechText) {
                    problems.push({
                        path: `${base}.speechText`,
                        message: 'Vorlesefeld braucht einen Text oder eine Audiodatei',
                    });
                }
            }
            if (item.type === 'task' && item.mode === 'generated' && item.resolved.length === 0) {
                problems.push({
                    path: `${base}.resolved`,
                    message: 'Erzeugte Aufgabe ohne gespeichertes Ergebnis (resolved)',
                });
            }
        });
    });
    // Textfluss und Selbstkontrolle verweisen auf Felder, die es geben muss.
    doc.pages.forEach((page, pageIndex) => {
        page.items.forEach((item, itemIndex) => {
            const base = `pages[${pageIndex}].items[${itemIndex}]`;
            if ((item.type === 'text' || item.type === 'bubble') && item.flowTo) {
                const target = itemTypes.get(item.flowTo);
                if (!target) {
                    problems.push({
                        path: `${base}.flowTo`,
                        message: `Zielfeld "${item.flowTo}" für den Textfluss fehlt`,
                    });
                }
                else if (target !== 'text' && target !== 'bubble' && target !== 'linedText') {
                    problems.push({
                        path: `${base}.flowTo`,
                        message: 'Textfluss ist nur in Text-, Blasen- oder Lineaturfelder möglich',
                    });
                }
            }
            if (item.type === 'selfCheck') {
                item.linkedItemIds.forEach((linked, i) => {
                    if (!itemIds.has(linked)) {
                        problems.push({
                            path: `${base}.linkedItemIds[${i}]`,
                            message: `Verknüpftes Feld "${linked}" fehlt im Dokument`,
                        });
                    }
                });
            }
        });
    });
    for (const [groupId, group] of Object.entries(doc.groups)) {
        if (group.id !== groupId) {
            problems.push({
                path: `groups.${groupId}.id`,
                message: `Gruppen-ID "${group.id}" passt nicht zum Schlüssel "${groupId}"`,
            });
        }
        group.memberIds.forEach((memberId, i) => {
            if (!itemIds.has(memberId)) {
                problems.push({
                    path: `groups.${groupId}.memberIds[${i}]`,
                    message: `Gruppenmitglied "${memberId}" gibt es nicht`,
                });
            }
        });
    }
    for (const [frameId, frame] of Object.entries(doc.frames)) {
        if (frame.id !== frameId) {
            problems.push({
                path: `frames.${frameId}.id`,
                message: `Rahmen-ID "${frame.id}" passt nicht zum Schlüssel "${frameId}"`,
            });
        }
        [...frame.header, ...frame.footer].forEach((row, rowIndex) => {
            row.blocks.forEach((block, blockIndex) => {
                if (block.kind === 'image' && !doc.assets[block.assetId]) {
                    problems.push({
                        path: `frames.${frameId}.rows[${rowIndex}].blocks[${blockIndex}].assetId`,
                        message: `Asset "${block.assetId}" fehlt im Dokument`,
                    });
                }
            });
        });
    }
    for (const [assetId, asset] of Object.entries(doc.assets)) {
        if (asset.id !== assetId) {
            problems.push({
                path: `assets.${assetId}.id`,
                message: `Asset-ID "${asset.id}" passt nicht zum Schlüssel "${assetId}"`,
            });
        }
    }
    return problems;
}
/** Strukturschema ohne Referenzprüfung. */
export const documentSchema = worksheetDocumentSchema;
/** Prüft Struktur und Referenzen, ohne zu werfen. */
export function safeParseDocument(input) {
    const result = documentSchema.safeParse(input);
    if (!result.success) {
        return { ok: false, problems: formatIssues(result.error) };
    }
    const problems = checkReferences(result.data);
    if (problems.length > 0) {
        return { ok: false, problems };
    }
    return { ok: true, document: result.data };
}
/** Wie {@link safeParseDocument}, wirft aber eine {@link DocumentValidationError}. */
export function parseDocument(input) {
    const result = safeParseDocument(input);
    if (!result.ok) {
        throw new DocumentValidationError(result.problems);
    }
    return result.document;
}
const ALLOWED_FONTS = new Set(FONT_IDS);
/**
 * Vorbereitung für den Import (A.8): unbekannte Schriften werden auf die
 * Standardschrift gesetzt. Unbekannte Felder verwirft das Schema selbst.
 */
export function replaceUnknownFonts(value, fallback, replaced = []) {
    if (Array.isArray(value)) {
        const mapped = value.map((entry) => replaceUnknownFonts(entry, fallback, replaced).value);
        return { value: mapped, replaced };
    }
    if (isRecord(value)) {
        const out = {};
        for (const [key, entry] of Object.entries(value)) {
            if ((key === 'family' || key === 'defaultFont') &&
                typeof entry === 'string' &&
                !ALLOWED_FONTS.has(entry)) {
                replaced.push(entry);
                out[key] = fallback;
                continue;
            }
            out[key] = replaceUnknownFonts(entry, fallback, replaced).value;
        }
        return { value: out, replaced };
    }
    return { value, replaced };
}
//# sourceMappingURL=validate.js.map